import {
  DateDropdownFilterEntry,
  DateFilterEntry,
  DateRangeFilterEntry,
  DateRangeWithPresetsFilterEntry,
  type FilterEntryComponentProps,
  MultiSelectFilterEntry,
  PaginatedMultiSelectFilterEntry,
  PaginatedSingleSelectFilterEntry,
  SingleSelectFilterEntry,
  YearSliderFilterEntry,
} from "./FilterEntryComponents";
import type { FilterType } from "./filters.types";

import type { useQueryFilterType } from "@/hooks/query/useQueryParams";
import type { Any } from "@/interfaces/globals";

interface FilterEntryProps<T, P> {
  filterKey: string;
  value: Any;
  side?: "top" | "right" | "bottom" | "left";
  filters: FilterType<T, P>;
  filterOperation: useQueryFilterType;
}

type FilterEntryRenderer = (
  props: FilterEntryComponentProps<Any, Any>,
) => JSX.Element;

interface FilterEntryResolver {
  matches: (props: FilterEntryComponentProps<Any, Any>) => boolean;
  Component: FilterEntryRenderer;
}

const BOOLEAN_FILTER_RENDERERS: Record<string, FilterEntryRenderer> = {
  date: DateFilterEntry,
  dateRange: DateRangeFilterEntry,
  dateDropdown: DateDropdownFilterEntry,
};

const FILTER_ENTRY_RESOLVERS: FilterEntryResolver[] = [
  {
    matches: ({ filterKey }) => filterKey === "dateRangeWithPresets",
    Component: DateRangeWithPresetsFilterEntry,
  },
  {
    matches: ({ value, filterKey }) =>
      (typeof value === "boolean" || typeof value === "object") &&
      Boolean(BOOLEAN_FILTER_RENDERERS[filterKey]),
    Component: (props): JSX.Element => {
      const Component = BOOLEAN_FILTER_RENDERERS[props.filterKey];

      return <Component {...props} />;
    },
  },
  {
    matches: ({ value }) => value?.multiSelect,
    Component: MultiSelectFilterEntry,
  },
  {
    matches: ({ value }) => value?.isPaginatedMultiSelect,
    Component: PaginatedMultiSelectFilterEntry,
  },
  {
    matches: ({ value }) => value?.isPaginatedSingleSelect,
    Component: PaginatedSingleSelectFilterEntry,
  },
  {
    matches: ({ value }) => value?.isDateDropdown,
    Component: DateDropdownFilterEntry,
  },
  {
    matches: ({ filterKey }) => filterKey === "yearSliderSelector",
    Component: YearSliderFilterEntry,
  },
];

/**
 * Renders a single filter entry through the registered filter component map.
 *
 * @param {FilterEntryProps<T, P>} props - Filter entry configuration.
 * @returns {JSX.Element | null} Matching filter input or null for unsupported boolean filters.
 */
const FilterEntry = <T, P>(
  props: FilterEntryProps<T, P>,
): JSX.Element | null => {
  const filterEntryProps = props as FilterEntryComponentProps<Any, Any>;
  const resolver = FILTER_ENTRY_RESOLVERS.find((entryResolver) =>
    entryResolver.matches(filterEntryProps),
  );

  if (resolver) {
    const Component = resolver.Component;

    return <Component {...filterEntryProps} />;
  }

  if (typeof props.value === "boolean") {
    return null;
  }

  return <SingleSelectFilterEntry {...filterEntryProps} />;
};

export default FilterEntry;
